// src/admin/components/apartments/ApartmentForm.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  RiSaveLine,
  RiArrowLeftLine,
  RiCheckLine,
  RiErrorWarningLine,
  RiRulerLine,
  RiBuilding3Line,
  RiLayoutMasonryLine,
  RiBox3Line,
  RiLightbulbLine,
} from 'react-icons/ri';
import {
  createApartment,
  getApartment,
  updateApartment,
  getAllBuildings,
  getAllApartmentTypes,
} from '../../../models/apartmentModel';
import { useTheme } from '../../../contexts/ThemeContext';

const ApartmentForm = ({ isEdit = false }) => {
  const { t, i18n } = useTranslation();
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const { id } = useParams();
  const isRTL = i18n.language === 'ar' || i18n.language === 'ku';

  const [loading, setLoading]       = useState(isEdit);
  const [saving, setSaving]         = useState(false);
  const [error, setError]           = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [touched, setTouched]       = useState({});
  const [buildings, setBuildings]   = useState([]);
  const [apartmentTypes, setApartmentTypes] = useState([]);

  // Keeping backend keys intact (floor, apartmentNumber, etc.)
  const [formData, setFormData] = useState({
    floor: '',
    apartmentNumber: '',
    size: '',
    type: '',
    status: 'vacant',
    isFurnished: false,
    buildingId: '',
    ownerId: '',
  });

  const validationMessages = {
    floor_required:            t('floor_required')            || 'Block/Zone number is required',
    apartment_number_required: t('apartment_number_required') || 'Showroom unit number is required',
    size_required:             t('size_required')             || 'Size is required',
    type_required:             t('type_required')             || 'Type is required',
    building_required:         t('building_required')         || 'Complex is required',
    invalid_size:              t('invalid_size')              || 'Size must be greater than 0',
  };

  const statusOptions = [
    { value: 'vacant',      label: t('vacant')            || 'Vacant / Unsold' },
    { value: 'occupied',    label: t('occupied')           || 'Rented / Sold' },
    { value: 'maintenance', label: t('under_maintenance')  || 'Under Maintenance' },
  ];

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const [buildingsResponse, typesResponse] = await Promise.all([
          getAllBuildings(),
          getAllApartmentTypes(),
        ]);
        setBuildings(buildingsResponse.buildings || []);
        const typesList = typesResponse.types?.map(type => ({ id: type.type, name: type.type })) || [];
        setApartmentTypes(typesList.length > 0 ? typesList : [
          { id: 'Small',        name: 'Small' },
          { id: 'Medium',       name: 'Medium' },
          { id: 'Large',        name: 'Large' },
          { id: 'Extra Large',  name: 'Extra Large' },
          { id: 'Cold Storage', name: 'Cold Storage' },
          { id: 'Drive-In',     name: 'Drive-In' },
        ]);
      } catch (err) {
        console.error('Error fetching initial data:', err);
        setError('Failed to load form data');
      }
    };
    fetchInitialData();
  }, []);

  useEffect(() => {
    const fetchApartmentData = async () => {
      if (isEdit && id) {
        try {
          setLoading(true);
          const { apartment } = await getApartment(id);
          setFormData({
            floor:           apartment.floor.toString()          || '',
            apartmentNumber: apartment.apartmentNumber           || '',
            size:            apartment.size?.toString()          || '',
            type:            apartment.type                      || '',
            status:          apartment.status                    || 'vacant',
            isFurnished:     apartment.isFurnished               || false,
            buildingId:      apartment.buildingId?.toString()    || '',
            ownerId:         apartment.ownerId?.toString()       || '',
          });
          setError(null);
        } catch (err) {
          console.error('Error fetching showroom data:', err);
          setError(err.message || t('failed_load_showroom_data', 'Failed to load showroom data'));
        } finally {
          setLoading(false);
        }
      }
    };
    fetchApartmentData();
  }, [isEdit, id]);

  const validateField = (name, value) => {
    switch (name) {
      case 'floor':           return !String(value).trim() ? validationMessages.floor_required            : null;
      case 'apartmentNumber': return !String(value).trim() ? validationMessages.apartment_number_required : null;
      case 'size':            return !String(value).trim() ? validationMessages.size_required : parseFloat(value) <= 0 ? validationMessages.invalid_size : null;
      case 'type':            return !String(value).trim() ? validationMessages.type_required             : null;
      case 'buildingId':      return !value               ? validationMessages.building_required          : null;
      default:                return null;
    }
  };

  const handleBlur = ({ target: { name, value } }) => {
    setTouched(p => ({ ...p, [name]: true }));
    setFormErrors(p => ({ ...p, [name]: validateField(name, value) }));
  };

  const handleChange = ({ target: { name, value, type, checked } }) => {
    const val = type === 'checkbox' ? checked : value;
    setFormData(p => ({ ...p, [name]: val }));
    if (touched[name]) setFormErrors(p => ({ ...p, [name]: validateField(name, val) }));
  };

  const validateForm = () => {
    const errs = {}; const touch = {};
    Object.keys(formData).forEach(k => { touch[k] = true; errs[k] = validateField(k, formData[k]); });
    setTouched(touch); setFormErrors(errs);
    return !Object.values(errs).some(Boolean);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      document.querySelector('.error-field')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    try {
      setSaving(true);
      const apartmentData = {
        floor:           parseInt(formData.floor),
        apartmentNumber: formData.apartmentNumber,
        size:            parseFloat(formData.size),
        type:            formData.type,
        status:          formData.status,
        isFurnished:     formData.isFurnished,
        buildingId:      parseInt(formData.buildingId),
        ownerId:         formData.ownerId ? parseInt(formData.ownerId) : null,
      };
      isEdit ? await updateApartment(id, apartmentData) : await createApartment(apartmentData);
      navigate('/admin/apartments');
    } catch (err) {
      console.error('Error saving showroom:', err);
      setError(err.message || `Failed to ${isEdit ? 'update' : 'create'} showroom`);
      window.scrollTo(0, 0);
    } finally {
      setSaving(false);
    }
  };

  /* ── Field Renderers ──────────────────────────────────────────────── */
  const renderField = (name, label, type = 'text', icon, placeholder, disabled = false, required = true, min = null) => {
    const hasError = touched[name] && formErrors[name];
    const isValid  = touched[name] && !formErrors[name] && formData[name] !== '';
    return (
      <div className={hasError ? 'error-field' : ''}>
        <label className={`block mb-1.5 text-sm font-semibold ${isDarkMode ? 'text-zinc-300' : 'text-zinc-700'}`}>
          {label} {required && <span className="text-red-500">*</span>}
        </label>
        <div className="relative">
          <div className={`absolute inset-y-0 start-0 flex items-center ps-3.5 pointer-events-none ${isDarkMode ? 'text-zinc-500' : 'text-zinc-400'}`}>
            {icon}
          </div>
          <input
            type={type} name={name} value={formData[name]}
            onChange={handleChange} onBlur={handleBlur}
            disabled={disabled} min={min} placeholder={placeholder}
            className={`w-full ps-10 pe-10 py-2.5 text-sm rounded-lg border transition-all focus:outline-none focus:ring-2 focus:ring-red-500/40 ${
              isDarkMode ? 'bg-zinc-800 border-zinc-700 text-white focus:border-red-500' : 'bg-zinc-50 border-zinc-200 text-zinc-900 focus:border-red-500'
            } ${hasError ? 'border-red-500' : isValid ? 'border-emerald-500' : ''} ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
          />
          {hasError && <div className="absolute inset-y-0 end-0 flex items-center pe-3 text-red-500 pointer-events-none"><RiErrorWarningLine size={17}/></div>}
          {isValid  && <div className="absolute inset-y-0 end-0 flex items-center pe-3 text-emerald-500 pointer-events-none"><RiCheckLine size={17}/></div>}
        </div>
        {hasError && <p className="mt-1.5 text-xs font-medium text-red-500">{formErrors[name]}</p>}
      </div>
    );
  };

  const renderSelect = (name, label, options, icon, required = true, placeholder = null) => {
    const hasError = touched[name] && formErrors[name];
    const isValid  = touched[name] && !formErrors[name] && formData[name] !== '';
    return (
      <div className={hasError ? 'error-field' : ''}>
        <label className={`block mb-1.5 text-sm font-semibold ${isDarkMode ? 'text-zinc-300' : 'text-zinc-700'}`}>
          {label} {required && <span className="text-red-500">*</span>}
        </label>
        <div className="relative">
          <div className={`absolute inset-y-0 start-0 flex items-center ps-3.5 pointer-events-none ${isDarkMode ? 'text-zinc-500' : 'text-zinc-400'}`}>
            {icon}
          </div>
          <select
            name={name} value={formData[name]}
            onChange={handleChange} onBlur={handleBlur}
            className={`w-full ps-10 pe-10 py-2.5 text-sm rounded-lg appearance-none bg-no-repeat border transition-all focus:outline-none focus:ring-2 focus:ring-red-500/40 ${
              isDarkMode ? 'bg-zinc-800 border-zinc-700 text-white focus:border-red-500' : 'bg-zinc-50 border-zinc-200 text-zinc-900 focus:border-red-500'
            } ${hasError ? 'border-red-500' : isValid ? 'border-emerald-500' : ''}`}
            style={{
              backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
              backgroundPosition: isRTL ? 'left 0.5rem center' : 'right 0.5rem center',
              backgroundSize: '1.5em 1.5em',
            }}
          >
            {placeholder && <option value="">{placeholder}</option>}
            {options.map(opt => (
              <option key={opt.id || opt.value} value={opt.id || opt.value}>
                {opt.name || opt.label}
              </option>
            ))}
          </select>
          {hasError && <div className="absolute inset-y-0 end-8 flex items-center text-red-500 pointer-events-none"><RiErrorWarningLine size={17}/></div>}
          {isValid  && <div className="absolute inset-y-0 end-8 flex items-center text-emerald-500 pointer-events-none"><RiCheckLine size={17}/></div>}
        </div>
        {hasError && <p className="mt-1.5 text-xs font-medium text-red-500">{formErrors[name]}</p>}
      </div>
    );
  };

  const renderCheckbox = (name, label, icon) => (
    <div
      className={`flex items-center p-3 rounded-lg border cursor-pointer transition-colors ${
        formData[name]
          ? isDarkMode ? 'border-yellow-500/40 bg-yellow-500/5' : 'border-yellow-400/40 bg-yellow-50'
          : isDarkMode ? 'border-zinc-700 hover:border-zinc-600' : 'border-zinc-200 hover:border-zinc-300'
      }`}
      onClick={() => setFormData(p => ({ ...p, [name]: !p[name] }))}
    >
      <input
        type="checkbox" id={name} name={name} checked={formData[name]}
        onChange={handleChange}
        onClick={e => e.stopPropagation()}
        className={`h-5 w-5 rounded cursor-pointer ${isDarkMode ? 'bg-zinc-800 border-zinc-600 text-red-500' : 'bg-zinc-50 border-zinc-300 text-red-600'}`}
      />
      <label htmlFor={name} className={`flex items-center ms-3 text-sm font-semibold cursor-pointer select-none ${isDarkMode ? 'text-zinc-200' : 'text-zinc-800'}`}>
        {icon && <span className={`me-2 ${formData[name] ? 'text-yellow-500' : isDarkMode ? 'text-zinc-500' : 'text-zinc-400'}`}>{icon}</span>}
        {label}
      </label>
    </div>
  );

  /* ── Loading ─────────────────────────────────────────────────────── */
  if (loading) return (
    <div dir={isRTL?'rtl':'ltr'} className={`rounded-xl border shadow-lg p-16 text-center ${isDarkMode ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-zinc-200'}`}>
      <div className="flex flex-col items-center gap-4">
        <div className="h-12 w-12 animate-spin rounded-full border-4 border-red-500 border-t-transparent"/>
        <p className={`text-lg font-medium ${isDarkMode ? 'text-zinc-300' : 'text-zinc-600'}`}>
          {t('loading_apartment_data') || 'Loading showroom data…'}
        </p>
      </div>
    </div>
  );

  /* ════════════════════════════════════════════════════════════════════ */
  return (
    <div dir={isRTL?'rtl':'ltr'} className={`rounded-xl border shadow-lg overflow-hidden ${isDarkMode ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-zinc-200'}`}>

      {/* Gradient accent */}
      <div className="h-1.5 w-full bg-gradient-to-r from-red-600 via-orange-500 to-red-600"/>

      {/* Header */}
      <div className={`p-5 md:p-6 border-b ${isDarkMode ? 'bg-zinc-900/50 border-zinc-800' : 'bg-zinc-50 border-zinc-100'}`}>
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className={`p-2.5 rounded-lg ${isDarkMode ? 'bg-red-900/30 text-red-400' : 'bg-red-50 text-red-600'}`}>
              <RiBox3Line size={24}/>
            </div>
            <div>
              <h2 className={`text-xl font-bold tracking-tight ${isDarkMode ? 'text-white' : 'text-zinc-900'}`}>
                {isEdit ? t('edit_apartment') || 'Configure Showroom' : t('add_new_apartment') || 'Log New Showroom'}
              </h2>
              <p className={`text-sm mt-0.5 ${isDarkMode ? 'text-zinc-400' : 'text-zinc-500'}`}>
                {isEdit ? t('update_showroom_desc', 'Update this showroom unit\'s specifications.') : t('register_showroom_desc', 'Register a new showroom unit in the complex.')}
              </p>
            </div>
          </div>
          <button onClick={()=>navigate('/admin/apartments')}
            className={`p-2 rounded-full transition-colors shrink-0 ${isDarkMode ? 'bg-zinc-800 text-zinc-400 hover:text-white hover:bg-zinc-700' : 'bg-zinc-100 text-zinc-500 hover:bg-zinc-200'}`}
            title="Back to List">
            <RiArrowLeftLine size={20} className="rtl:rotate-180"/>
          </button>
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <div className={`mx-6 mt-6 p-4 rounded-lg flex items-center gap-3 ${isDarkMode ? 'bg-red-900/20 text-red-400 border border-red-900/50' : 'bg-red-50 text-red-700 border border-red-200'}`}>
          <RiErrorWarningLine className="text-xl shrink-0"/>
          <span className="text-sm font-medium">{error}</span>
        </div>
      )}

      <form onSubmit={handleSubmit} className="p-6">
        <div className="max-w-4xl space-y-8">

          {/* Section: Location & Identity */}
          <div className="space-y-5">
            <h3 className={`text-xs font-black uppercase tracking-widest pb-2 border-b ${isDarkMode ? 'text-zinc-500 border-zinc-800' : 'text-zinc-400 border-zinc-200'}`}>
              {t('location_and_identity', 'Location & Identity')}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {renderSelect(
                'buildingId',
                t('building') || 'Complex / Park',
                buildings,
                <RiBuilding3Line size={18}/>,
                true,
                t('select_building') || 'Select parent complex…'
              )}
              {renderField(
                'floor',
                t('row_section') || 'Block / Zone Number',
                'number',
                <RiLayoutMasonryLine size={18}/>,
                t('enter_row_section') || 'e.g., 1'
              )}
              {renderField(
                'apartmentNumber',
                t('apartment_number') || 'Showroom Unit Number',
                'text',
                <RiBox3Line size={18}/>,
                t('enter_apartment_number') || 'e.g., WH-104'
              )}
            </div>
          </div>

          {/* Section: Specifications */}
          <div className="space-y-5">
            <h3 className={`text-xs font-black uppercase tracking-widest pb-2 border-b ${isDarkMode ? 'text-zinc-500 border-zinc-800' : 'text-zinc-400 border-zinc-200'}`}>
              {t('specifications', 'Specifications')}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {renderSelect(
                'type',
                t('apartment_type') || 'Showroom Class',
                apartmentTypes,
                <RiBox3Line size={18}/>,
                true,
                t('select_apartment_type') || 'Select structural class…'
              )}
              {renderField(
                'size',
                t('size_sqm') || 'Internal Floor Size (m²)',
                'number',
                <RiRulerLine size={18}/>,
                t('enter_size') || 'e.g., 500',
                false, true, 1
              )}
              {renderSelect(
                'status',
                t('status') || 'Current Status',
                statusOptions,
                <RiCheckLine size={18}/>
              )}
            </div>

            {/* Utilities checkbox */}
            <div className="mt-2">
              {renderCheckbox(
                'isFurnished',
                t('furnished') || 'Utilities Active (Power / Climate Control)',
                <RiLightbulbLine size={18}/>
              )}
            </div>
          </div>
        </div>

        {/* Action buttons */}
        <div className={`mt-10 pt-6 border-t flex justify-end gap-3 ${isDarkMode ? 'border-zinc-800' : 'border-zinc-200'}`}>
          <button type="button" onClick={()=>navigate('/admin/apartments')} disabled={saving}
            className={`px-5 py-2.5 text-sm font-semibold rounded-lg transition-colors ${isDarkMode ? 'bg-zinc-800 text-zinc-300 hover:bg-zinc-700' : 'bg-zinc-100 text-zinc-700 hover:bg-zinc-200'}`}>
            {t('cancel') || 'Cancel'}
          </button>
          <button type="submit" disabled={saving}
            className="px-6 py-2.5 text-sm font-semibold rounded-lg bg-red-600 text-white hover:bg-red-700 shadow-md shadow-red-500/20 flex items-center gap-2 min-w-[140px] justify-center">
            {saving ? (
              <><div className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white"/>{t('saving')||'Saving…'}</>
            ) : (
              <><RiSaveLine size={16}/>{isEdit ? t('update')||'Update Showroom' : t('save')||'Log Showroom'}</>
            )}
          </button>
        </div>
      </form>

      <style dangerouslySetInnerHTML={{__html:`
        @keyframes fadeIn { from{opacity:0;transform:translateY(-4px)} to{opacity:1;transform:translateY(0)} }
        .animate-fadeIn { animation: fadeIn 0.2s ease-out forwards; }
      `}}/>
    </div>
  );
};

export default ApartmentForm;